import { ArrowDownward, ArrowUpward, ChevronRight, EventOutlined, PlaceOutlined } from "@mui/icons-material";
import { Box, ButtonBase, Collapse, Typography } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { AppContext } from "../../context/AppContext";
import { OwnerConsoleViewController } from "../../controllers/OwnerConsoleViewController";
import { formatAsPercentage, formatCmpctNumber } from "../../helpers/utils";
import { ChartEntry, ReportConfiguration } from "../../interfaces/AppDefinitions";
import { AgentCard, AgentDock, AgentNote } from "./AgentPanel";
import HomeUpdates from "./HomeUpdates";
import ValueChart from "./ValueChart";
import EquityPanel from "../mortgage/EquityPanel";
import AssistantChat from "../assistant/AssistantChat";
import { useMortgage } from "../mortgage/MortgageContext";
import { DOWN, INK, INK_2, LINE, MUTED, PAGE, UP, cardSx, isValidURL, labelSx, withAlpha } from "./shared";

// The redesigned homeowner report (reportLayout MODERN). Same data and settings as the original:
// value first, a clearer chart, and the agent always in reach (sidebar on desktop, bottom bar on phones).
// The report is often shown in an iframe. Above this width the whole layout scales up with the frame
// (text, cards and chart together) instead of sitting small in the middle of a wide page.
const DESIGN_WIDTH = 1240;
const MAX_SCALE = 1.8;

function useFitScale() {
	const measure = () => {
		const width = window.innerWidth;
		return width > DESIGN_WIDTH ? Math.min(width / DESIGN_WIDTH, MAX_SCALE) : 1;
	};
	const [scale, setScale] = useState(measure);
	useEffect(() => {
		const onResize = () => setScale(measure());
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);
	return scale;
}

// Paints the page behind the report so a frame taller than the report shows the report background.
function usePageBackground(color: string) {
	useEffect(() => {
		const previous = document.body.style.backgroundColor;
		document.body.style.backgroundColor = color;
		return () => {
			document.body.style.backgroundColor = previous;
		};
	}, [color]);
}

export default function ModernReport() {
	const { appState } = useContext(AppContext);
	const { report_configuration, currentAddrId, info } = appState;
	const report = report_configuration?.find((_) => _?.addrId === currentAddrId);
	const agent = info.agent;
	const showMortgage = info.mortgage !== undefined && !!agent.customMortgageProvider;
	const mortgageInsights = useMortgage();
	const scale = useFitScale();
	usePageBackground(PAGE);

	return (
		<Box
			sx={{
				// Zoom also scales margins, so divide to cancel the body's 15px padding exactly. The height
				// follows the content (no 100vh minimum) so an embedding page can size its iframe to fit;
				// the page background fills any frame space below it.
				m: `calc(-15px / ${scale})`,
				zoom: scale,
				backgroundColor: PAGE,
				color: INK,
				textAlign: "left",
				pb: { xs: "96px", md: 0 },
			}}
		>
			<Box sx={{ px: { xs: "16px", md: "32px" }, py: { xs: "16px", md: "28px" } }}>
				{report_configuration.length > 1 && <PropertyTabs />}
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) 330px" },
						gap: { xs: "16px", md: "24px" },
						alignItems: "start",
					}}
				>
					<Box
						sx={{ display: "flex", flexDirection: "column", gap: { xs: "16px", md: "20px" }, minWidth: 0 }}
					>
						{report && <ValueHero report={report} />}
						{report && <EquityPanel variant="modern" estimatedValue={report.reportData.estimatedValue} />}
						{report && (
							<Box sx={{ ...cardSx, overflow: "hidden" }}>
								<Box sx={{ p: { xs: "16px 14px 4px", md: "22px 26px 8px" } }}>
									<ValueChart
										data={report.reportData.data}
										height={260}
										mortgage={mortgageInsights?.mortgage?.history}
									/>
								</Box>
								<StatsRow report={report} />
							</Box>
						)}
						<AssistantChat />
						<Box sx={{ display: { xs: "block", md: "none" } }}>
							<UpdatedCard />
						</Box>
						<LogoCard display={{ xs: "flex", md: "none" }} />
						<Box sx={{ display: { xs: "block", md: "none" }, ...cardSx, p: "18px" }}>
							<AgentNote withAvatar />
						</Box>
						<Box sx={{ display: { xs: "flex", md: "none" }, flexDirection: "column", gap: "16px" }}>
							<HomeUpdates />
							{showMortgage && <MortgageLink />}
						</Box>
					</Box>
					<Box
						component="aside"
						sx={{
							display: { xs: "none", md: "flex" },
							flexDirection: "column",
							gap: "16px",
							position: "sticky",
							top: "20px",
						}}
					>
						<LogoCard display="flex" />
						<AgentCard />
						<UpdatedCard />
						<HomeUpdates />
						{showMortgage && <MortgageLink />}
					</Box>
				</Box>
				<Footer />
			</Box>
			<AgentDock />
		</Box>
	);
}

// The logo from Report Settings ("Logo URL") in its own card above the agent, like the original layout:
// top of the sidebar on desktop, above the agent's note on phones. Unlike the original it does not depend
// on the Owner Console logo setting. Hidden if the image does not load.
function LogoCard(props: { display: string | Record<string, string> }) {
	const { appState } = useContext(AppContext);
	const { agent } = appState.info;
	const src = agent.customOCLogo?.trim();
	const [failed, setFailed] = useState<string>();
	if (!src || !isValidURL(src) || failed === src) return null;
	return (
		<Box
			sx={{
				...cardSx,
				display: props.display,
				alignItems: "center",
				justifyContent: "center",
				p: { xs: "20px", md: "28px 24px" },
			}}
		>
			<Box
				component="img"
				src={src}
				alt={agent.businessName || "Logo"}
				onError={() => setFailed(src)}
				sx={{
					display: "block",
					width: "100%",
					maxWidth: "240px",
					height: "auto",
					maxHeight: "80px",
					objectFit: "contain",
				}}
			/>
		</Box>
	);
}

// When the estimate was last refreshed, in its own small card.
function UpdatedCard() {
	const { appState } = useContext(AppContext);
	const updated = appState.last_updated ? new Date(appState.last_updated) : null;
	if (!updated || isNaN(updated.getTime())) return null;
	return (
		<Box sx={{ ...cardSx, display: "flex", alignItems: "center", gap: "12px", p: "14px 16px" }}>
			<Box
				sx={{
					display: "grid",
					placeItems: "center",
					width: 36,
					height: 36,
					borderRadius: "10px",
					backgroundColor: PAGE,
					color: INK_2,
					flex: "none",
				}}
			>
				<EventOutlined sx={{ fontSize: 20 }} />
			</Box>
			<Box sx={{ minWidth: 0 }}>
				<Typography sx={{ fontSize: "12px", color: MUTED }}>Estimate updated</Typography>
				<Typography sx={{ fontSize: "14px", fontWeight: 700, color: INK }}>
					{updated.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
				</Typography>
			</Box>
		</Box>
	);
}

function PropertyTabs() {
	const { appState, appDispatch } = useContext(AppContext);
	const brand = appState.colorPallete["scheme1"];
	return (
		<Box
			role="tablist"
			aria-label="Your properties"
			sx={{ display: "flex", gap: "8px", overflowX: "auto", mb: { xs: "16px", md: "20px" }, pb: "2px" }}
		>
			{appState.report_configuration.map((config) => {
				const active = config.addrId === appState.currentAddrId;
				return (
					<ButtonBase
						key={config.addrId}
						role="tab"
						aria-selected={active}
						onClick={() => appDispatch({ type: "SET_CURRENT_ADDR_ID", value: config.addrId })}
						sx={{
							flex: "none",
							px: "14px",
							py: "8px",
							borderRadius: "99px",
							fontSize: "13px",
							fontWeight: 600,
							textTransform: "capitalize",
							border: `1px solid ${active ? brand : LINE}`,
							backgroundColor: active ? withAlpha(brand, 0.08) : "#fff",
							color: active ? brand : INK_2,
						}}
					>
						{config.street}
					</ButtonBase>
				);
			})}
		</Box>
	);
}

function ValueHero(props: { report: ReportConfiguration }) {
	const { appState } = useContext(AppContext);
	const { info, colorPallete } = appState;
	const { street, city, state, reportData } = props.report;
	const { estimatedValue, lowerRange, upperRange, monthlyChange } = reportData;
	const address = [street, city, state].filter(Boolean).join(", ");
	const showRange = info.agent.displayValuationRange && upperRange > lowerRange;
	const showChange = info.agent.displayMonthlyChange && typeof monthlyChange === "number" && monthlyChange !== 0;
	const previous = estimatedValue - monthlyChange;
	const changePct = previous > 0 ? Math.abs(monthlyChange / previous) : 0;
	const tone = monthlyChange > 0 ? UP : DOWN;
	const marker = showRange
		? Math.min(1, Math.max(0, (estimatedValue - lowerRange) / (upperRange - lowerRange))) * 100
		: 50;

	return (
		<Box
			sx={{
				...cardSx,
				p: { xs: "20px 18px", md: "28px 30px" },
				display: "grid",
				gridTemplateColumns: {
					xs: "minmax(0, 1fr)",
					md: showRange ? "minmax(0, 1fr) minmax(0, 260px)" : "1fr",
				},
				gap: { xs: "18px", md: "24px 36px" },
				alignItems: "end",
			}}
		>
			<Box sx={{ minWidth: 0 }}>
				<Typography
					sx={{
						display: "flex",
						alignItems: "center",
						gap: "4px",
						fontSize: "13px",
						color: INK_2,
						textTransform: "capitalize",
					}}
				>
					<PlaceOutlined sx={{ fontSize: 16 }} />
					{address}
				</Typography>
				<Typography sx={{ ...labelSx, mt: "16px" }}>Estimated value</Typography>
				<Typography
					component="p"
					sx={{
						fontSize: { xs: "38px", md: "48px" },
						fontWeight: 800,
						letterSpacing: "-.035em",
						lineHeight: 1.05,
						mt: "2px",
						fontVariantNumeric: "tabular-nums",
					}}
				>
					{formatCmpctNumber(estimatedValue ?? 0)}
				</Typography>
				{showChange && (
					<Box
						sx={{
							display: "inline-flex",
							alignItems: "center",
							gap: "4px",
							mt: "10px",
							px: "10px",
							py: "3px",
							borderRadius: "99px",
							fontSize: "12px",
							fontWeight: 700,
							color: tone.fg,
							backgroundColor: tone.bg,
						}}
					>
						{monthlyChange > 0 ? (
							<ArrowUpward sx={{ fontSize: 14 }} />
						) : (
							<ArrowDownward sx={{ fontSize: 14 }} />
						)}
						{formatCmpctNumber(Math.abs(monthlyChange))}
						{changePct > 0 && <span>· {formatAsPercentage(changePct)}</span>}
						<Box component="span" sx={{ fontWeight: 500, opacity: 0.85 }}>
							since last month
						</Box>
					</Box>
				)}
			</Box>
			{showRange && (
				<Box sx={{ pb: { md: "6px" } }}>
					<Typography sx={{ ...labelSx, mb: "10px" }}>Likely range</Typography>
					<Box
						sx={{
							position: "relative",
							height: "8px",
							borderRadius: "99px",
							background: `linear-gradient(90deg, ${withAlpha(colorPallete["scheme5"], 0.25)}, ${withAlpha(
								colorPallete["scheme1"],
								0.55,
							)}, ${withAlpha(colorPallete["scheme5"], 0.25)})`,
						}}
					>
						<Box
							sx={{
								position: "absolute",
								top: "50%",
								left: `${marker}%`,
								width: 16,
								height: 16,
								borderRadius: "50%",
								border: "3px solid #fff",
								backgroundColor: colorPallete["scheme1"],
								boxShadow: "0 1px 4px rgba(0,0,0,.25)",
								transform: "translate(-50%, -50%)",
							}}
						/>
					</Box>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							mt: "8px",
							fontSize: "12px",
							color: MUTED,
						}}
					>
						<Box>
							<Box sx={{ color: INK, fontWeight: 700, fontSize: "13px" }}>
								{formatCmpctNumber(lowerRange)}
							</Box>
							Low
						</Box>
						<Box sx={{ textAlign: "right" }}>
							<Box sx={{ color: INK, fontWeight: 700, fontSize: "13px" }}>
								{formatCmpctNumber(upperRange)}
							</Box>
							High
						</Box>
					</Box>
				</Box>
			)}
		</Box>
	);
}

// Change over the last 12 months of chart data, when the data goes back that far.
function yearChange(data: ChartEntry[]) {
	if (!data || data.length < 2) return null;
	const last = data[data.length - 1];
	const lastDate = new Date(last.x);
	const yearAgo = data.find((point) => {
		const d = new Date(point.x);
		return d.getUTCFullYear() === lastDate.getUTCFullYear() - 1 && d.getUTCMonth() === lastDate.getUTCMonth();
	});
	// The server sends y as a string with two decimals.
	const now = Number(last.y);
	const then = Number(yearAgo?.y);
	if (!then || !isFinite(now)) return null;
	return (now - then) / then;
}

function StatsRow(props: { report: ReportConfiguration }) {
	const { appState } = useContext(AppContext);
	const { agent } = appState.info;
	const { gainLoss, annualROI, data } = props.report.reportData;
	const signed = (text: string, n: number) => (n > 0 ? `+${text}` : n < 0 ? `-${text}` : text);
	const stats: Array<[string, string]> = [];
	if (agent.displayGainLoss && gainLoss)
		stats.push([signed(formatCmpctNumber(Math.abs(gainLoss)), gainLoss), "Since you bought"]);
	if (agent.displayAnnualROI && annualROI)
		stats.push([signed(formatAsPercentage(Math.abs(annualROI)), annualROI), "Average yearly return"]);
	const year = yearChange(data);
	if (year !== null) stats.push([signed(formatAsPercentage(Math.abs(year)), year), "Past 12 months"]);
	if (!stats.length) return null;
	return (
		<Box
			sx={{
				display: "flex",
				flexWrap: "wrap",
				ml: "-1px",
			}}
		>
			{stats.map(([value, label]) => (
				<Box
					key={label}
					sx={{
						flex: "1 1 140px",
						borderTop: `1px solid ${LINE}`,
						borderLeft: `1px solid ${LINE}`,
						p: { xs: "12px 16px", md: "16px 26px" },
					}}
				>
					<Typography
						sx={{
							fontSize: { xs: "17px", md: "20px" },
							fontWeight: 700,
							fontVariantNumeric: "tabular-nums",
						}}
					>
						{value}
					</Typography>
					<Typography sx={{ fontSize: "12px", color: MUTED }}>{label}</Typography>
				</Box>
			))}
		</Box>
	);
}

function MortgageLink() {
	const { appState } = useContext(AppContext);
	const { info } = appState;
	async function openMortgage() {
		try {
			await OwnerConsoleViewController.TrackMortgageClick({ enc: appState.enc });
		} catch (err) {
			console.log(err);
		}
		window.open(`${info.agent.customMortgageProvider}?enc=${appState.enc}`, "width=600,height=600");
	}
	return (
		<ButtonBase
			onClick={openMortgage}
			sx={{
				...cardSx,
				justifyContent: "space-between",
				textAlign: "left",
				gap: "12px",
				p: "14px 16px",
				width: "100%",
			}}
		>
			<Box sx={{ minWidth: 0 }}>
				<Typography sx={{ fontWeight: 700, fontSize: "14px", color: INK }}>
					{info.agent.mortgageButtonText || "See mortgage options"}
				</Typography>
				{info.agent.mortgageRateText && (
					<Typography sx={{ fontSize: "12px", color: MUTED }}>{info.agent.mortgageRateText}</Typography>
				)}
			</Box>
			<ChevronRight sx={{ color: MUTED }} />
		</ButtonBase>
	);
}

function Footer() {
	const { appState } = useContext(AppContext);
	const { disclaimer, footerDisclaimer } = appState;
	const [open, setOpen] = useState(false);
	return (
		<Box sx={{ mt: { xs: "20px", md: "28px" }, fontSize: "12px", color: MUTED }}>
			<Typography sx={{ fontSize: "12px", color: MUTED, maxWidth: "70ch" }}>{disclaimer}</Typography>
			<ButtonBase
				onClick={() => setOpen(!open)}
				aria-expanded={open}
				sx={{ mt: "8px", fontSize: "12px", fontWeight: 600, color: INK_2, textDecoration: "underline" }}
			>
				{open ? "Hide full disclaimer" : "Read full disclaimer"}
			</ButtonBase>
			<Collapse in={open}>
				<Typography sx={{ fontSize: "12px", color: MUTED, mt: "8px", maxWidth: "90ch" }}>
					{footerDisclaimer}
				</Typography>
			</Collapse>
		</Box>
	);
}
